import { ExternalQuestion } from '@sm/question-widgets/respondent-survey';
import { QuestionAnswers, QuestionAnswer } from '~app/pages/SurveyTaking/types';
import { mapImplicitResponse } from './mapImplicitResponse';

import { GetCurrentSurveySessionQuery, RespApiAnswerInput } from '~lib/generatedGqlTypes';

/**
 * Function that maps an individual answer to the shape expected by the API, returned as an array.
 * Reference - expected shapes for each question type:
 * @see https://code.corp.surveymonkey.com/pages/devmonkeys/ResponsesAPI/?shell#the-answer-object
 */
const mapAnswerResponse = (
  { family, variant, values }: QuestionAnswer,
  responseBase: Partial<RespApiAnswerInput>
): Partial<RespApiAnswerInput>[] => {
  const responses: Partial<RespApiAnswerInput>[] = [];

  switch (family) {
    // single textbox & comment box
    case 'OPEN_ENDED': {
      switch (variant) {
        case 'SINGLE_LINE':
        case 'ESSAY': {
          if (values[0]?.value) {
            responses.push({ ...responseBase, answerText: values[0].value });
          }
        }
      }
      break;
    }
    // multiple choice, dropdown, & checkbox (& possibly single/many image choice, may need refinement)
    case 'MULTIPLE_CHOICE': {
      switch (variant) {
        case 'SINGLE_ANSWER_RADIO':
        case 'SINGLE_ANSWER_MENU':
        case 'MANY_ANSWERS_CHECKBOX':
        case 'SINGLE_ANSWER_IMAGE':
        case 'MANY_ANSWERS_IMAGE': {
          values.forEach(v => {
            if (v.id) {
              const response = { ...responseBase, answerId: v.id };
              if (v.type && (v.type === 'ANSWER' || v.type === 'COMMENT')) {
                response.answerText = v.value;
              }
              responses.push(response);
            }
          });
        }
      }
      break;
    }
  }
  return responses;
};

type MapSurveyAnswersArgsType = {
  questions: ExternalQuestion[];
  answers: QuestionAnswers;
  pageId: NonNullable<GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['currentPage']>['id'];
};

/**
 *
 * @param questions Array containing questions from current page
 * @param answers Object containing answers for the current page with questionId as the key
 * @param pageId Current survey page that user is responding to
 * @returns an array of responses matching the shape expected by mutation, empty responses discarded.
 */
export const mapSurveyAnswersToResponse = ({
  questions,
  answers,
  pageId,
}: MapSurveyAnswersArgsType): Partial<RespApiAnswerInput>[] => {
  return Object.values(answers).reduce<Partial<RespApiAnswerInput>[]>((responses, answer) => {
    /** The base response object containing properties to be included with each response */
    const responseBase: Partial<RespApiAnswerInput> = {
      pageId,
      questionId: answer.questionId,
    };

    const implicitResponse = mapImplicitResponse(answer.questionId, questions);
    if (implicitResponse.length) {
      responseBase.randomAssignmentId = implicitResponse[0].randomAssignmentVariableId;
    }

    return [...responses, ...mapAnswerResponse(answer, responseBase)];
  }, []);
};

export default mapSurveyAnswersToResponse;
